// src/components/Login.js
// verticalstack — S1.B (May 15, 2026): 2-step signup with workspace provisioning
// Closes AS-221 (empty workspace on signup) and AS-239 (no wizard routing).
//
// S1.B-fix (May 16, 2026): Replaced post-signup window.location.href = '/'
// reload with a Step 3 success screen + Sign In button. The reload was
// landing users on /landing.html instead of the React Shell.
//
// S1.B-fix-2 (May 16, 2026): Added 1.5s pre-sign-in delay in Step 3 —
// insufficient. Diagnostic proved fresh-tab sign-in works while same-tab
// sign-in fails, confirming the bug is hook-state staleness, not Firestore
// index lag. Reverted.
//
// S1.B-fix-3 (May 16, 2026, FINAL for S1): Step 3 button now reloads to
// /login (not '/'). Full page reload clears all React hook state, and
// /login has no auth-gate redirect to landing.html. User re-enters
// credentials on the fresh login page and lands in Shell normally.
// Underlying hooks-layer fix (useAuth/useWorkspace re-init on subsequent
// sign-in) is S2 scope.
//
// SIGNUP FLOW:
//   Step 1: email + password → createUserWithEmailAndPassword → Auth user exists
//   Step 2: workspace name → write /users/{uid}, /workspaces/{newId},
//           /clientMembers/{uid_newId} (as 'invited' then immediately 'active'),
//           → advance to Step 3 success screen
//   Step 3: success screen with "Sign In to your workspace" button →
//           signInWithEmailAndPassword(email, password) → navigate('/')
//
// Why the Step 3 success screen instead of an auto-reload:
//   - useAuth's onAuthStateChanged fires the instant the Auth user is created
//     in Step 1, before Step 2 writes /clientMembers.
//   - It calls getUserWorkspaces CF, which returns [] (no member doc yet).
//   - useWorkspace then locks initialized=true with no workspaces.
//   - Even after Step 2's writes succeed, useWorkspace won't re-init.
//   - The original S1.B used window.location.href = '/' to reset hook state
//     via full reload, but that landed users on /landing.html (S2 routing bug).
//   - signInWithEmailAndPassword triggers a fresh onAuthStateChanged AFTER
//     /clientMembers is already written, so useWorkspace initializes correctly
//     with the new workspace present. navigate('/') then routes cleanly.
//
// clientMembers double-write (May 15, 2026 — required by deployed rules):
//   The DEPLOYED firestore rules (S37 v2, Apr 29 2026) restrict /clientMembers
//   self-create to status='invited' ONLY. The update rule permits status='active'
//   when resource.data.userId == uid(). So we create as 'invited' then immediately
//   update to 'active'. Same flow, two-write pattern, matches deployed rules
//   without requiring a rules change.
//
// Partial-failure handling:
//   Step 2 Firestore writes fail → user stays on Step 2, can retry (newUid preserved).
//   Step 3 sign-in fails → user stays on Step 3, can click Sign In button again,
//   or switch to the Sign In tab and authenticate manually with the same credentials.
//
// Sign In path is unchanged from S16B.
// Reset password path is unchanged from S16B.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import brand from '../config/brand';
import './Login.css';

// ── Helpers ────────────────────────────────────────────────────────────

function slugify(input) {
  return (input || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function shortSuffix() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < 4; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

function buildWorkspaceId(entityName) {
  const slug = slugify(entityName);
  const safe = slug || 'workspace';
  return `${safe}-${shortSuffix()}`;
}

function isValidEntityName(name) {
  const trimmed = (name || '').trim();
  if (trimmed.length < 2) return false;
  const alphaNumCount = (trimmed.match(/[a-zA-Z0-9]/g) || []).length;
  return alphaNumCount >= 2;
}

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin');
  const [signupStep, setSignupStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [entityName, setEntityName] = useState('');
  const [newUid, setNewUid] = useState(null);
  const [createdWorkspaceName, setCreatedWorkspaceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('info');

  function switchMode(nextMode) {
    setMode(nextMode);
    setSignupStep(1);
    setEntityName('');
    setNewUid(null);
    setCreatedWorkspaceName('');
    setMsg('');
  }

  async function handleSignInToWorkspace() {
    // Step 3 action: full page reload to /login, which resets all React
    // hook state (useAuth, useWorkspace). The user then signs in from a
    // clean state and the workspace fetches correctly.
    //
    // Why a reload instead of in-component signInWithEmailAndPassword
    // (S1.B-fix-3, May 16, 2026):
    //   The hooks layer (useAuth + useWorkspace) locks initialized=true on
    //   the FIRST onAuthStateChanged event of a session. Step 1's auth-user
    //   creation triggers that event before /clientMembers exists, so
    //   useWorkspace caches [] for the session. Subsequent in-tab sign-in
    //   doesn't re-trigger the workspace fetch — the hook sees
    //   "already initialized" and skips. Diagnostic confirmed: same creds
    //   in a fresh tab land cleanly in Shell. The fix is to reset React
    //   state via full reload.
    //
    // Why /login instead of '/' (the original S1.B target):
    //   The S1.A.3 SW fix removed the SW TypeError, but '/' is still
    //   redirected to /landing.html for transient auth states by the
    //   routing/auth-gate layer (S2 scope). /login has no such redirect —
    //   the user lands on the Login component fresh, hook state cleared,
    //   and can sign in normally with credentials they just used.
    //
    // The user's email is preserved in component state but lost on reload.
    // They'll need to re-enter both email and password on the fresh /login
    // page. UX tradeoff accepted; the alternative is fixing the hooks
    // layer which is S2 scope.
    if (busy) return;
    setBusy(true);
    setMsg('');
    window.location.href = '/login';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg('');

    try {
      if (mode === 'signin') {
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/');
        return;
      }

      if (mode === 'reset') {
        await sendPasswordResetEmail(auth, email);
        setMsgType('success');
        setMsg('Password reset link sent. Check your email.');
        setTimeout(() => switchMode('signin'), 2500);
        return;
      }

      if (mode === 'signup' && signupStep === 1) {
        // ── STEP 1: create Auth user ──────────────────────────────
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        setNewUid(cred.user.uid);
        setSignupStep(2);
        setMsgType('info');
        setMsg('Account created. One more step — name your workspace.');
        return;
      }

      if (mode === 'signup' && signupStep === 2) {
        // ── STEP 2: provision workspace + clientMembers + user doc ──
        if (!newUid) {
          throw new Error('Internal: missing UID from step 1.');
        }

        if (!isValidEntityName(entityName)) {
          setMsgType('error');
          setMsg('Workspace name must include at least 2 letters or numbers.');
          setBusy(false);
          return;
        }

        const trimmedEntity = entityName.trim();
        const newWorkspaceId = buildWorkspaceId(trimmedEntity);
        const memberId = `${newUid}_${newWorkspaceId}`;

        try {
          // ── Write 1: /users/{uid} — global identity doc ──
          await setDoc(doc(db, 'users', newUid), {
            email,
            displayName: '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            updatedBy: 'signup',
            homeWorkspaceId: newWorkspaceId,
            status: 'active',
            emailVerified: false,
            globalRole: 'user',
            userId: newUid,
          });

          // ── Write 2: /workspaces/{newWorkspaceId} — the entity itself ──
          await setDoc(doc(db, 'workspaces', newWorkspaceId), {
            id: newWorkspaceId,
            workspaceName: trimmedEntity,
            label: trimmedEntity,
            name: trimmedEntity,
            ownerUid: newUid,           // required by deployed rule for create
            kind: 'entity',
            type: 'LLC',
            plan: 'starter',
            verticalType: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: newUid,
          });

          // ── Write 3a: /clientMembers/{uid_wid} as 'invited' ──
          // Deployed rules (S37 v2, Apr 29 2026) restrict self-create to
          // status='invited'. Then we immediately update to 'active' below.
          await setDoc(doc(db, 'clientMembers', memberId), {
            userId: newUid,
            workspaceId: newWorkspaceId,
            roles: ['owner_pm'],
            status: 'invited',          // required by deployed create rule
            isHomeWorkspace: true,
            addedAt: serverTimestamp(),
            addedBy: newUid,
          });

          // ── Write 3b: update same /clientMembers doc to 'active' ──
          // The update rule permits this when resource.data.userId == uid()
          // (self-update is allowed). Result: doc lands at status='active'
          // so getUserWorkspaces CF's `where('status', '==', 'active')` matches.
          await updateDoc(doc(db, 'clientMembers', memberId), {
            status: 'active',
            updatedAt: serverTimestamp(),
          });
        } catch (writeErr) {
          console.error('[Login] Step 2 Firestore write failed:', writeErr);
          setMsgType('error');
          setMsg(
            friendlyFirestoreError(writeErr.code) ||
            'Could not create your workspace. Tap "Create Workspace" again to retry. ' +
            'If the problem persists, contact hello@verticalstack.ai.'
          );
          setBusy(false);
          return;
        }

        // All writes succeeded — advance to Step 3 success screen
        // (replaces the S1.B window.location.href reload that landed on landing.html).
        setCreatedWorkspaceName(trimmedEntity);
        setSignupStep(3);
        setMsgType('success');
        setMsg('');
        setBusy(false);
        return;
      }
    } catch (err) {
      console.error('[Login] error:', err);
      setMsgType('error');
      setMsg(
        friendlyAuthError(err.code) ||
        friendlyFirestoreError(err.code) ||
        err.message ||
        'Something went wrong.'
      );
    } finally {
      setBusy(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────

  const showSignupStepIndicator = mode === 'signup';

  return (
    <div className="vs-login-page" style={{ background: brand.colors.navBg }}>
      <div className="vs-login-card" style={{ background: brand.colors.cardBg }}>
        <div className="vs-login-brand">
          <div className="vs-wordmark">
            <span style={{ color: brand.colors.cyan }}>vertical</span>
            <span style={{ color: brand.colors.navy }}>stack</span>
          </div>
          <div className="vs-tagline" style={{ color: brand.colors.textMuted }}>
            {brand.tagline}
          </div>
        </div>

        <div className="vs-login-tabs">
          <button
            type="button"
            onClick={() => switchMode('signin')}
            className={`vs-tab ${mode === 'signin' ? 'vs-tab-active' : ''}`}
            style={mode === 'signin' ? { color: brand.colors.navy, borderColor: brand.colors.cyan } : {}}
            disabled={busy}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => switchMode('signup')}
            className={`vs-tab ${mode === 'signup' ? 'vs-tab-active' : ''}`}
            style={mode === 'signup' ? { color: brand.colors.navy, borderColor: brand.colors.cyan } : {}}
            disabled={busy}
          >
            Create Account
          </button>
        </div>

        {showSignupStepIndicator && (
          <div className="vs-signup-steps">
            <div className={`vs-signup-step ${signupStep >= 1 ? 'vs-signup-step-active' : ''}`}>
              <span className="vs-signup-step-dot" style={{
                background: signupStep >= 1 ? brand.colors.cyan : brand.colors.border,
                color: signupStep >= 1 ? '#fff' : brand.colors.textMuted,
              }}>
                {signupStep > 1 ? '✓' : '1'}
              </span>
              <span style={{ color: signupStep >= 1 ? brand.colors.navy : brand.colors.textMuted }}>
                Account
              </span>
            </div>
            <div className="vs-signup-step-divider" style={{
              background: signupStep >= 2 ? brand.colors.cyan : brand.colors.border,
            }} />
            <div className={`vs-signup-step ${signupStep >= 2 ? 'vs-signup-step-active' : ''}`}>
              <span className="vs-signup-step-dot" style={{
                background: signupStep >= 2 ? brand.colors.cyan : brand.colors.border,
                color: signupStep >= 2 ? '#fff' : brand.colors.textMuted,
              }}>
                {signupStep > 2 ? '✓' : '2'}
              </span>
              <span style={{ color: signupStep >= 2 ? brand.colors.navy : brand.colors.textMuted }}>
                Workspace
              </span>
            </div>
            <div className="vs-signup-step-divider" style={{
              background: signupStep >= 3 ? brand.colors.cyan : brand.colors.border,
            }} />
            <div className={`vs-signup-step ${signupStep >= 3 ? 'vs-signup-step-active' : ''}`}>
              <span className="vs-signup-step-dot" style={{
                background: signupStep >= 3 ? brand.colors.cyan : brand.colors.border,
                color: signupStep >= 3 ? '#fff' : brand.colors.textMuted,
              }}>
                {signupStep >= 3 ? '✓' : '3'}
              </span>
              <span style={{ color: signupStep >= 3 ? brand.colors.navy : brand.colors.textMuted }}>
                Done
              </span>
            </div>
          </div>
        )}

        {msg && (
          <div
            className="vs-msg"
            style={{
              background: msgType === 'error' ? brand.colors.dangerBg
                       : msgType === 'success' ? brand.colors.successBg
                       : brand.colors.infoBg,
              color: msgType === 'error' ? brand.colors.dangerText
                   : msgType === 'success' ? brand.colors.successText
                   : brand.colors.infoText,
            }}
          >
            {msg}
          </div>
        )}

        {/* ── Step 3: success screen (signup only) ────────────────────── */}
        {mode === 'signup' && signupStep === 3 ? (
          <div className="vs-login-form">
            <div style={{
              padding: '20px',
              textAlign: 'center',
              background: brand.colors.successBg,
              color: brand.colors.successText,
              borderRadius: '8px',
              marginBottom: '16px',
            }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>
                ✓ Workspace created
              </div>
              <div style={{ fontSize: '14px' }}>
                <strong>{createdWorkspaceName}</strong> is ready.
              </div>
              <div style={{ fontSize: '13px', marginTop: '6px', opacity: 0.85 }}>
                Sign in with your new email and password to enter your workspace.
              </div>
            </div>

            <button
              type="button"
              onClick={handleSignInToWorkspace}
              disabled={busy}
              className="vs-submit"
              style={{
                background: brand.colors.navy,
                color: brand.colors.textOnDark,
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? 'Loading…' : 'Continue to Sign In →'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="vs-login-form">
            {(mode !== 'signup' || signupStep === 1) && (
              <>
                <label className="vs-label">
                  <span style={{ color: brand.colors.textSecondary }}>Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="vs-input"
                    style={{ borderColor: brand.colors.border, color: brand.colors.textPrimary }}
                    disabled={busy}
                  />
                </label>

                {mode !== 'reset' && (
                  <label className="vs-label">
                    <span style={{ color: brand.colors.textSecondary }}>Password</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder={mode === 'signup' ? '6+ characters' : ''}
                      className="vs-input"
                      style={{ borderColor: brand.colors.border, color: brand.colors.textPrimary }}
                      disabled={busy}
                    />
                  </label>
                )}
              </>
            )}

            {mode === 'signup' && signupStep === 2 && (
              <label className="vs-label">
                <span style={{ color: brand.colors.textSecondary }}>
                  Workspace name
                </span>
                <input
                  type="text"
                  value={entityName}
                  onChange={(e) => setEntityName(e.target.value)}
                  required
                  maxLength={60}
                  placeholder="Smith Family Holdings, LLC"
                  className="vs-input"
                  style={{ borderColor: brand.colors.border, color: brand.colors.textPrimary }}
                  disabled={busy}
                  autoFocus
                />
                <span className="vs-help" style={{ color: brand.colors.textMuted }}>
                  The LLC, partnership, or trade name you'll use to manage properties.
                  You can rename this later.
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={busy}
              className="vs-submit"
              style={{
                background: brand.colors.navy,
                color: brand.colors.textOnDark,
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? 'Working…'
                : mode === 'signin' ? 'Sign In'
                : mode === 'reset'  ? 'Send Reset Link'
                : (mode === 'signup' && signupStep === 1) ? 'Continue →'
                : 'Create Workspace ✓'}
            </button>

            {mode === 'signin' && (
              <button
                type="button"
                onClick={() => switchMode('reset')}
                className="vs-link"
                style={{ color: brand.colors.cyan }}
                disabled={busy}
              >
                Forgot password?
              </button>
            )}

            {mode === 'reset' && (
              <button
                type="button"
                onClick={() => switchMode('signin')}
                className="vs-link"
                style={{ color: brand.colors.cyan }}
                disabled={busy}
              >
                ← Back to sign in
              </button>
            )}
          </form>
        )}

        <div className="vs-footer" style={{ color: brand.colors.textMuted }}>
          {brand.name} · <a href={`mailto:${brand.supportEmail}`}
            style={{ color: brand.colors.cyan, textDecoration: 'none' }}>
            {brand.supportEmail}
          </a>
        </div>
      </div>
    </div>
  );
}

function friendlyAuthError(code) {
  const map = {
    'auth/invalid-email': 'That email address looks invalid.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with that email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Email or password is incorrect.',
    'auth/email-already-in-use': 'An account with that email already exists.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/too-many-requests': 'Too many attempts. Try again in a few minutes.',
    'auth/network-request-failed': 'Network issue — check your connection.',
  };
  return map[code] || null;
}

function friendlyFirestoreError(code) {
  const map = {
    'permission-denied':
      'Permission denied creating your workspace. Contact hello@verticalstack.ai with this error code.',
    'unavailable':
      'Database is temporarily unavailable. Tap "Create Workspace" again in a few seconds.',
    'deadline-exceeded':
      'Connection timed out. Tap "Create Workspace" again to retry.',
    'unauthenticated':
      'Session expired. Refresh the page and try again.',
    'cancelled':
      'Workspace creation was cancelled. Tap "Create Workspace" again to retry.',
    'aborted':
      'Workspace creation was aborted. Tap "Create Workspace" again to retry.',
    'resource-exhausted':
      'Service quota exceeded. Try again in a few minutes.',
  };
  return map[code] || null;
}